import { prefersReducedMotion } from '@scripts/lenis';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

export interface ProductChangeDetail {
  index: number;
}

export interface ProductTransitionDetail {
  /** シリーズ切替の区間に入っている間 true。3D 側はこの間バックルを隠す */
  hidden: boolean;
}

export interface ProductPinDetail {
  /** ピン(スクロールジャック)が有効か。3D 側はこれが false の間は隠す */
  pinned: boolean;
}

// pin (スクロールジャック) をやめてパネルを縦積みにする条件。
// prefers-reduced-motion に加えて、モバイル(狭い)と背の低いビューポート。
// pin 中は sticky な h-svh の内側に 2 カラム(仕様表 + 作図枠)を収める前提で、
// 高さが足りないと内容が切れてしまうため、その場合は素直に縦スクロールへ倒す。
// 縦積みの見た目は ProductSection.astro の section[data-reduced] が担う。
// PIN_QUERY は NO_PIN_QUERY のちょうど否定 (両者は排他かつ網羅)。gsap.matchMedia
// でこの 2 つを出し分け、リサイズで境界をまたいだら演出ごと組み替える
const NO_PIN_QUERY =
  '(prefers-reduced-motion: reduce), (max-width: 1023px), (max-height: 640px)';
const PIN_QUERY =
  '(prefers-reduced-motion: no-preference) and (min-width: 1024px) and (min-height: 641px)';
/** ロード時点でピンが無効か (3D の初回ロード要否の判定に使う) */
export const productsPinDisabled = window.matchMedia(NO_PIN_QUERY).matches;

const SEGMENT = 10;
// 最初のシリーズはフェードインなしで始まり体感が短くなるため、追加でホールドする。
// 最初のパネルだけ SEGMENT + FIRST_HOLD (= 11) ぶん、以降は SEGMENT (= 10) ぶん
// スクロールする。セクション全体の高さ (ProductSection.astro の h-[...]) を
// FIRST_HOLD + パネル数 * SEGMENT で割り振るので、ここを変えると各パネルの
// スクロール長の比率が変わる
const FIRST_HOLD = SEGMENT / 10;
// シリーズ切替のクロスフェード(時間ベース)。前パネルが抜けきってから
// 次パネルをスライドインさせる
const FADE_OUT = 0.4;
const FADE_IN = 0.6;

/** パネル i がアクティブになるスクロール上の時刻 (SEGMENT 単位) */
const startOf = (i: number) => (i === 0 ? 0 : FIRST_HOLD + i * SEGMENT);

/** 各パネル内の画像スライダー(クロスフェード + ドット)。
    onUserNav はユーザー操作(ドット/左右クリック)のたびに呼ばれ、
    呼び出し側で自動再生を一時停止させるためのフック */
function createSlider(panel: HTMLElement, onUserNav: () => void) {
  const slides = [...panel.querySelectorAll<HTMLElement>('[data-slide]')];
  const dots = [...panel.querySelectorAll<HTMLElement>('[data-slider-dot]')];
  const prevBtn = panel.querySelector<HTMLElement>('[data-slider-prev]');
  const nextBtn = panel.querySelector<HTMLElement>('[data-slider-next]');
  // 図番 (Fig. ##) の末尾。表示中の画像番号に追従させる
  const numberEl = panel.querySelector<HTMLElement>('[data-slider-number]');
  let current = 0;

  const goTo = (next: number, instant = false) => {
    if (next === current) return;
    if (instant || prefersReducedMotion) {
      gsap.set(slides[current], { opacity: 0 });
      gsap.set(slides[next], { opacity: 1 });
    } else {
      gsap.to(slides[current], { opacity: 0, duration: 0.8 });
      gsap.to(slides[next], { opacity: 1, duration: 0.8 });
    }
    dots[current]?.setAttribute('aria-current', 'false');
    dots[next]?.setAttribute('aria-current', 'true');
    if (numberEl) numberEl.textContent = String(next + 1).padStart(2, '0');
    current = next;
  };

  const advance = () => goTo((current + 1) % slides.length);
  // パネルが見えていないとき(セクション離脱/シリーズ切替)に 1 枚目へ戻す。
  // フェードさせると復帰時に途中経過が見えてしまうので即時に切り替える
  const reset = () => goTo(0, true);
  const retreat = () => goTo((current - 1 + slides.length) % slides.length);

  dots.forEach((dot, j) =>
    dot.addEventListener('click', () => {
      onUserNav();
      goTo(j);
    }),
  );
  nextBtn?.addEventListener('click', () => {
    onUserNav();
    advance();
  });
  prevBtn?.addEventListener('click', () => {
    onUserNav();
    retreat();
  });

  // タッチでのスワイプ送り。横移動が縦移動を上回り、かつ一定距離を超えた
  // ときだけ前後へ送る(タップは既存の左右クリックゾーンが担う)。touchmove
  // は passive のまま(preventDefault しない)なので縦スクロールは阻害しない
  const viewport = panel.querySelector<HTMLElement>('[data-slider]');
  if (viewport) {
    const SWIPE_THRESHOLD = 40;
    let startX = 0;
    let startY = 0;
    let tracking = false;

    viewport.addEventListener(
      'touchstart',
      (e) => {
        if (e.touches.length !== 1) {
          tracking = false;
          return;
        }
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        tracking = true;
      },
      { passive: true },
    );

    viewport.addEventListener(
      'touchend',
      (e) => {
        if (!tracking) return;
        tracking = false;
        const touch = e.changedTouches[0];
        const dx = touch.clientX - startX;
        const dy = touch.clientY - startY;
        // 横方向かつ十分な距離のときだけスワイプとみなす
        if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) <= Math.abs(dy)) {
          return;
        }
        onUserNav();
        if (dx < 0) advance();
        else retreat();
        // スワイプ直後に発火しうる click(左右ゾーン)を短時間だけ握りつぶし、
        // 二重送りを防ぐ。発火しなくても timeout で必ず外す
        const suppressClick = (ev: Event) => {
          ev.stopPropagation();
          ev.preventDefault();
        };
        viewport.addEventListener('click', suppressClick, { capture: true });
        window.setTimeout(() => {
          viewport.removeEventListener('click', suppressClick, {
            capture: true,
          });
        }, 350);
      },
      { passive: true },
    );
  }

  return { advance, reset };
}

export function initProducts() {
  const section = document.querySelector<HTMLElement>('[data-products]');
  if (!section) return;

  const panels = [
    ...section.querySelectorAll<HTMLElement>('[data-product-panel]'),
  ];
  const toggles = [
    ...section.querySelectorAll<HTMLElement>('[data-slider-toggle]'),
  ];
  // 再生/一時停止ボタンの外周リング。進捗 0→1 を stroke-dashoffset 1→0 に
  // 写して、暗めのグレーが円を一周したら次の画像へ送る。状態が全パネル共有
  // なのでリングも全パネルぶんまとめて同じ進捗にしておく
  const rings = [
    ...section.querySelectorAll<SVGPathElement>('[data-slider-progress]'),
  ];

  // 手動ナビ(ドット/左右クリック)はピンの有無に関係なく使えるよう一度だけ
  // 用意する。ユーザー操作で自動再生を止めるフックはピン中だけ意味を持つので、
  // 差し替え可能な参照にしておき、ピン設定時に実体を入れる(縦積み時は no-op)
  let onUserNav = () => {};
  const sliders = panels.map((panel) => createSlider(panel, () => onUserNav()));

  // ピン ⇄ 縦積み を viewport の変化で切り替える。gsap.matchMedia はクエリが
  // 外れた瞬間に、その中で作った ScrollTrigger・トゥイーン・gsap.set のインライン
  // スタイルを自動で巻き戻す。GSAP 管理外(IntersectionObserver / イベント)は
  // setup が返すクリーンアップで畳む。NO_PIN と PIN はちょうど排他なので、
  // 常にどちらか一方だけが有効になる
  const mm = gsap.matchMedia();

  const notifyPin = (pinned: boolean) => {
    const detail: ProductPinDetail = { pinned };
    window.dispatchEvent(new CustomEvent('soak:products-pin', { detail }));
  };

  // --- 縦積みモード: data-reduced を付け、無意味な自動再生ボタンを隠す ---
  mm.add(NO_PIN_QUERY, () => {
    section.setAttribute('data-reduced', '');
    toggles.forEach((toggle) => (toggle.hidden = true));
    notifyPin(false);
    // 巻き戻しは PIN 側 setup が担う(data-reduced 除去・トグル復帰)
  });

  // --- ピンモード: スクロールジャックの演出一式を組む ---
  mm.add(PIN_QUERY, () => {
    section.removeAttribute('data-reduced');
    toggles.forEach((toggle) => (toggle.hidden = false));
    notifyPin(true);

    // このモード限定のイベントはまとめて外せるよう AbortController に束ねる
    const ac = new AbortController();
    const { signal } = ac;

    let activeIndex = 0;
    // 自動再生の状態。ドット/左右クリック等のユーザー操作、または
    // 一時停止ボタンで止まる。状態は全パネルで共有する
    const AUTOPLAY_INTERVAL = 4000;
    let paused = false;
    // スライダーが完全にビューポートへ入るまで自動再生を始めない
    let visible = false;

    const syncToggles = () => {
      toggles.forEach((toggle) => {
        toggle.dataset.playing = paused ? 'false' : 'true';
        toggle.setAttribute(
          'aria-label',
          paused ? 'スライドショーを再生' : 'スライドショーを一時停止',
        );
      });
    };

    // カウントダウンの進捗。setInterval ではなくトゥイーンで持つことで、
    // 一時停止(位置保持)・再開・リセットをリングの見た目と一体で扱える
    const progress = { value: 0 };
    const renderRings = () => {
      const offset = String(1 - progress.value);
      rings.forEach((ring) => {
        ring.style.strokeDashoffset = offset;
      });
    };
    const countdown = gsap.to(progress, {
      value: 1,
      duration: AUTOPLAY_INTERVAL / 1000,
      ease: 'none',
      paused: true,
      onUpdate: renderRings,
      onComplete: () => {
        // アクティブなパネルのスライダーだけ自動送りする
        sliders[activeIndex]?.advance();
        countdown.restart();
      },
    });

    const syncCountdown = () => {
      if (!paused && visible) countdown.play();
      else countdown.pause();
    };

    // 進捗を 0 に戻す (pause(0) で先頭へシーク)。再生条件が揃っていれば
    // そのまま最初からカウントし直す
    const resetCountdown = () => {
      countdown.pause(0);
      renderRings();
      syncCountdown();
    };

    const setPaused = (value: boolean) => {
      if (paused === value) return;
      paused = value;
      syncToggles();
      syncCountdown();
    };

    // 左右クリックやドット操作が入ったら自動再生を止め、リングも空に戻す
    onUserNav = () => {
      setPaused(true);
      resetCountdown();
    };

    toggles.forEach((toggle) =>
      toggle.addEventListener('click', () => setPaused(!paused), { signal }),
    );

    // スライダーが画面内に「完全に」収まってから自動再生を開始する。
    // 画像ボックスだけでなく下部の番号ナビ/再生ボタンまで含めて全体が
    // ビューポートに入っていることを条件にする(画像は見えているが操作 UI は
    // まだ画面外、という半端な状態では再生させない)。監視対象は最初のパネル
    // のもの(全パネルが同じ位置に重なるため代表でよい)。半端な表示に
    // なった時点でカウントダウンだけ止め、画像位置は保持する(1 枚目への
    // リセットはセクションが完全に抜けきってから。下の sectionIo が担当)
    const region = [
      section.querySelector<HTMLElement>('[data-slider]'),
      section.querySelector<HTMLElement>('[data-slider-dots]'),
    ].filter((el): el is HTMLElement => el != null);
    let io: IntersectionObserver | null = null;
    if (region.length) {
      const ratios = new WeakMap<Element, number>();
      io = new IntersectionObserver(
        (entries) => {
          for (const entry of entries)
            ratios.set(entry.target, entry.intersectionRatio);
          // 監視要素がすべて完全表示(ratio ≈ 1)のときだけ再生可
          const nowVisible = region.every(
            (el) => (ratios.get(el) ?? 0) >= 0.99,
          );
          if (nowVisible === visible) return;
          visible = nowVisible;
          resetCountdown();
        },
        { threshold: [0.99, 1] },
      );
      region.forEach((el) => io?.observe(el));
    } else {
      visible = true;
      syncCountdown();
    }

    // 1 枚目へのリセットは、セクション ([data-products]) が完全に
    // ビューポート外へ抜けきってから行う。セクションは (products.length +
    // 0.5) * 300svh と背高で、完全に非交差になるのは sticky の表示領域まで
    // 含めて全部が画面外へ出た時だけ。スライダーが少し欠けただけでは戻さず
    // 画像位置を保持し、上下どちらの離脱でも抜けきった時点で 1 枚目へ戻す
    const sectionIo = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) {
            sliders.forEach((slider) => slider.reset());
          }
        }
      },
      { threshold: 0 },
    );
    sectionIo.observe(section);

    const dispatchChange = (index: number) => {
      const detail: ProductChangeDetail = { index };
      window.dispatchEvent(new CustomEvent('soak:product-change', { detail }));
    };
    const dispatchTransition = (hidden: boolean) => {
      const detail: ProductTransitionDetail = { hidden };
      window.dispatchEvent(
        new CustomEvent('soak:product-transition', { detail }),
      );
    };

    // シリーズ切替: scrub ではなく、境界をまたいだ時点で時間ベースの
    // クロスフェードを一度だけ再生する。3D バックルは切替の頭で隠し
    // (soak:product-transition)、前パネルが抜けて見えなくなったところで
    // 据え直し (soak:product-change)、フェードイン完了後に再表示する。
    // 受け側の処理は choreography.ts
    let switchTl: gsap.core.Timeline | null = null;
    const switchTo = (index: number) => {
      const prev = activeIndex;
      activeIndex = index;
      // 新しいシリーズは 1 枚目からフルにカウントし直す。抜けていく側の
      // スライダーはフェードアウトが済んでから 1 枚目へ戻す(下の timeline)
      resetCountdown();
      switchTl?.kill();
      // 連続で境界を跨いだとき、キルされた途中状態のパネルが残らないよう
      // 対象の 2 枚以外は隠しておく
      panels.forEach((panel, i) => {
        if (i !== prev && i !== index) gsap.set(panel, { autoAlpha: 0 });
      });
      const content = panels[index]?.querySelector('[data-product-content]');
      switchTl = gsap.timeline();
      switchTl.call(() => dispatchTransition(true));
      switchTl.to(panels[prev], {
        autoAlpha: 0,
        duration: FADE_OUT,
        ease: 'sine.in',
      });
      switchTl.call(() => {
        dispatchChange(index);
        sliders[prev]?.reset();
      });
      switchTl.to(panels[index], {
        autoAlpha: 1,
        duration: FADE_IN,
        ease: 'sine.out',
      });
      switchTl.fromTo(
        content,
        { y: 24 },
        { y: 0, duration: FADE_IN, ease: 'sine.out', immediateRender: false },
        '<',
      );
      switchTl.call(() => dispatchTransition(false));
    };

    // sticky なビューポートの上を section 全体(1 シリーズ 300svh)で監視し、
    // パネルの境界をまたいだらアクティブを切り替える
    const total = FIRST_HOLD + panels.length * SEGMENT;
    ScrollTrigger.create({
      trigger: section,
      start: 'top top',
      end: 'bottom bottom',
      onUpdate: (self) => {
        const time = self.progress * total;
        let index = 0;
        for (let i = panels.length - 1; i > 0; i--) {
          if (time >= startOf(i)) {
            index = i;
            break;
          }
        }
        if (index !== activeIndex) switchTo(index);
      },
    });

    // 縦積みへ切り替わる時の後始末。ScrollTrigger・トゥイーン・gsap.set の
    // インラインスタイル(パネルの autoAlpha 等)は gsap.matchMedia が自動で
    // 巻き戻すので、ここでは GSAP 管理外のものだけ畳む
    return () => {
      ac.abort();
      io?.disconnect();
      sectionIo.disconnect();
      onUserNav = () => {};
      // 画像は 1 枚目へ戻し、リングの stroke-dashoffset(手書き)も空へ
      sliders.forEach((slider) => slider.reset());
      rings.forEach((ring) => {
        ring.style.strokeDashoffset = '1';
      });
    };
  });
}
